// Scanner Feature Types

export type RiskLevel = 'safe' | 'caution' | 'avoid' | 'unknown';
export type ProductType = 'food' | 'cosmetic';

export interface IngredientFlag {
  name: string;
  risk_level: RiskLevel;
  reason: string;
  category: string;
}

export interface NutritionFacts {
  energy_kcal?: number;
  energy_kj?: number;
  fat?: number;
  saturated_fat?: number;
  carbohydrates?: number;
  sugars?: number;
  fiber?: number;
  proteins?: number;
  salt?: number;
  sodium?: number;
  alcohol?: number;
}

export interface Product {
  barcode: string;
  name: string;
  brand: string;
  categories: string;
  image_url: string;
  image_small_url: string;
  ingredients_text: string;
  nutriscore_grade: string | null;
  nova_group: number | null;
  quantity: string;
  nutriments?: NutritionFacts;
}

export interface ProductSafety {
  overall_risk: RiskLevel;
  flagged_count: number;
  flagged_ingredients: IngredientFlag[];
}

export interface ProductLookupResponse {
  found: boolean;
  productType: ProductType;
  product?: Product;
  safety: ProductSafety;
  cached: boolean;
  scan_id?: string;
}

export interface ProductScan {
  id: string;
  user_id: string;
  product_id: string | null;
  barcode: string;
  scan_result: RiskLevel;
  flagged_ingredients: IngredientFlag[];
  pregnancy_ssw: number | null;
  product_type: ProductType;
  is_favorite: boolean;
  notes: string | null;
  scanned_at: string;
  // Joined from products table
  product?: Product;
}

// Risk level colors and labels
export const RISK_LEVELS: Record<RiskLevel, {
  label: string;
  labelDe: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: 'check' | 'alert-triangle' | 'x-circle' | 'help-circle';
}> = {
  safe: {
    label: 'Safe',
    labelDe: 'Unbedenklich',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    icon: 'check',
  },
  caution: {
    label: 'Caution',
    labelDe: 'Mit Vorsicht',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-500',
    icon: 'alert-triangle',
  },
  avoid: {
    label: 'Avoid',
    labelDe: 'Meiden',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-500',
    icon: 'x-circle',
  },
  unknown: {
    label: 'Unknown',
    labelDe: 'Unbekannt',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-500',
    icon: 'help-circle',
  },
};

// Suggestion types
export type SuggestionStatus = 'open' | 'in_progress' | 'closed';

export interface ProductSuggestion {
  id: string;
  user_id: string;
  brand: string;
  product_name: string;
  product_type: ProductType;
  barcode?: string;
  similar_found: boolean;
  notes?: string;
  status: SuggestionStatus;
  created_at: string;
}

export interface IngredientReport {
  id: string;
  user_id: string;
  ingredient_name: string;
  product_type: ProductType;
  found_in_product?: string;
  notes?: string;
  status: SuggestionStatus;
  created_at: string;
}

export const SUGGESTION_STATUS_LABELS: Record<SuggestionStatus, string> = {
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  closed: 'Erledigt',
};
